import { motion } from "framer-motion";
import { Home, Baby, Sparkles, HeartHandshake, MapPin, Award, Users, Heart } from "lucide-react";
import { useLocalizedContent } from "@/hooks/use-localized-content";
import { useTranslation } from "react-i18next";
import wellnessImg from "@/assets/wellness-lifestyle.webp";

/** Split into readable paragraphs. Respect author line breaks; if none,
 *  auto-group sentences (~2 sentences per paragraph). */
const paragraphize = (text: string): string[] => {
  if (!text) return [];
  const explicit = text
    .split(/\r?\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (explicit.length > 1) return explicit;

  const sentences = text.match(/[^.!?]+[.!?]+["']?\s*/g)?.map((s) => s.trim()) || [text];
  const chunks: string[] = [];
  const perChunk = sentences.length > 6 ? 3 : 2;
  for (let i = 0; i < sentences.length; i += perChunk) {
    chunks.push(sentences.slice(i, i + perChunk).join(" "));
  }
  return chunks;
};

const POINT_ICONS = [Home, Baby, Sparkles, HeartHandshake, MapPin];

const About = () => {
  const { c, content } = useLocalizedContent();
  const { t } = useTranslation();

  const aboutBadge = c("about_badge", "about.badge");
  const aboutTitle = c("about_title", "about.title");
  const aboutText = c("about_text", "about.text");
  const aboutImage = content.about_image || wellnessImg;
  const aboutCta = c("about_cta_text", "about.cta");
  const trustTitle = c("about_trust_title", "about.trustTitle");
  const trustSubtitle = c("about_trust_subtitle", "about.trustSubtitle");

  const stats = [
    { icon: Users, value: content.about_stat1_value || "+75", label: c("about_stat1_label", "about.stat1") },
    { icon: Award, value: content.about_stat2_value || "3+", label: c("about_stat2_label", "about.stat2") },
    { icon: Heart, value: content.about_stat3_value || "98%", label: c("about_stat3_label", "about.stat3") },
  ];

  const paragraphs = paragraphize(aboutText);
  const pointsRaw = c("about_points", "about.points");
  const points = pointsRaw.split(/\r?\n/).map((p) => p.trim()).filter(Boolean);

  const titleWords = aboutTitle.split(", ");
  const titleBefore = titleWords[0] + ",";
  const titleHighlight = titleWords.slice(1).join(", ");


  return (
    <section id="sobre" className="py-24 bg-secondary/30">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-20"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <stat.icon size={24} className="text-primary mx-auto mb-2" />
              <p className="font-display text-3xl md:text-4xl font-bold text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-primary/10 aspect-[4/5] max-w-md mx-auto md:mx-0">
              <img src={aboutImage} alt="Thaiane Freitas massoterapeuta certificada Mente Sã massagem relaxante aromaterapia gestão ansiedade bem-estar ao domicílio Lisboa Oeiras Cascais Paço de Arcos" className="w-full h-full object-cover" width={448} height={560} loading="lazy" />
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="absolute -bottom-4 -right-4 md:right-auto md:-left-4 rounded-2xl bg-card border border-border p-4 shadow-lg"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Award size={20} className="text-primary" />
                </div>
                <div>
                  <p className="font-display font-bold text-foreground text-sm">{trustTitle}</p>
                  <p className="text-xs text-muted-foreground">{trustSubtitle}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block rounded-full bg-accent px-4 py-1.5 text-xs font-semibold text-accent-foreground mb-4">
              {aboutBadge}
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-8 leading-tight">
              {titleBefore}{" "}
              <span className="text-gradient-wine">{titleHighlight}</span>
            </h2>

            <div className="space-y-5 mb-10">
              {aboutText
                .split(/\n\s*\n/)
                .map((p) => p.trim())
                .filter(Boolean)
                .map((paragraph, i) => (
                  <p
                    key={i}
                    className={
                      i === 0
                        ? "text-foreground text-lg md:text-xl leading-relaxed font-medium"
                        : "text-muted-foreground text-base md:text-[17px] leading-[1.85]"
                    }
                  >
                    {paragraph}
                  </p>
                ))}
            </div>

            {points.length > 0 && (
              <ul className="grid sm:grid-cols-2 gap-4 mb-10 pt-6 border-t border-border/60">
                {points.map((point, i) => {
                  const Icon = POINT_ICONS[i % POINT_ICONS.length];
                  return (
                    <li
                      key={point}
                      className="group flex items-start gap-3 rounded-2xl border border-border/60 bg-card/60 backdrop-blur-sm p-4 hover:border-primary/30 hover:bg-card transition-all duration-300"
                    >
                      <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                        <Icon size={16} className="text-primary" />
                      </span>
                      <span className="text-foreground/90 text-[14px] leading-snug pt-1.5 font-medium">
                        {point}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            <a
              href="#agendar"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground shadow-lg shadow-primary/20 hover:opacity-90 transition-opacity"
            >
              {aboutCta}
            </a>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default About;
